import { useState } from "react";
import { updateAccount, useAction } from "wasp/client/operations";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { CURRENCY } from "../shared/currency";

export interface ExpenseAccount {
  id: string;
  name: string;
  challengeCost?: number | null;
  activationFee?: number | null;
}

interface Props {
  account: ExpenseAccount;
  isDark: boolean;
}

const formatMoney = (amount: number) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: CURRENCY, maximumFractionDigits: 2 }).format(amount);

/** Somme des coûts d'achat portés par le compte (challenge + activation). */
export function accountExpenseTotal(account: ExpenseAccount): number {
  return (account.challengeCost ?? 0) + (account.activationFee ?? 0);
}

/** Rend `**texte**` en gras, comme la modale « compte cramé ». */
function renderBold(template: string) {
  return template.split("**").map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part,
  );
}

/**
 * Corbeille en bout de ligne du « Suivi par compte » : retire la dépense du
 * compte (coût du challenge et frais d'activation remis à `null`) sans toucher
 * au compte lui-même. Le PFM lit ces deux champs avec `?? 0` : une fois
 * l'action passée, l'invalidation des queries fait disparaître le montant des
 * totaux. Les charges liées ont leur propre suppression dans la modale.
 *
 * Rendu vide quand le compte ne porte aucune dépense : rien à supprimer.
 */
export default function AccountExpenseDeleteButton({ account, isDark }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const updateAccountFn = useAction(updateAccount);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const challengeCost = account.challengeCost ?? 0;
  const activationFee = account.activationFee ?? 0;
  const total = challengeCost + activationFee;
  if (total <= 0) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await updateAccountFn({ id: account.id, challengeCost: null, activationFee: null } as any);
      toast({ title: t.analytics.payoutsDeleteExpenseDone });
      setOpen(false);
    } catch (err: any) {
      toast({ title: t.analytics.payoutsEditError, description: err?.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const lines = [
    { label: t.analytics.payoutsChallengeCost, amount: challengeCost },
    { label: t.analytics.payoutsActivationFee, amount: activationFee },
  ].filter((l) => l.amount > 0);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex h-6 w-6 items-center justify-center rounded transition-colors ${isDark ? "text-gray-400 hover:bg-gray-700 hover:text-red-400" : "text-gray-500 hover:bg-gray-100 hover:text-red-600"}`}
        aria-label={`${t.analytics.payoutsDeleteExpenseAction} (${account.name})`}
        title={t.analytics.payoutsDeleteExpenseAction}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>

      <Dialog open={open} onOpenChange={(next) => { if (!deleting) setOpen(next); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive shrink-0" aria-hidden />
              <DialogTitle>{t.analytics.payoutsDeleteExpenseTitle}</DialogTitle>
            </div>
            <DialogDescription>
              {renderBold(t.analytics.payoutsDeleteExpenseBody.replace("{account}", account.name))}
            </DialogDescription>
          </DialogHeader>

          <ul className="divide-y divide-border rounded-md border border-border text-sm">
            {lines.map((l) => (
              <li key={l.label} className="flex items-center justify-between px-3 py-2">
                <span className="text-muted-foreground">{l.label}</span>
                <span className="font-mono font-semibold text-pnlNegative">-{formatMoney(l.amount)}</span>
              </li>
            ))}
            {lines.length > 1 && (
              <li className="flex items-center justify-between px-3 py-2 font-medium">
                <span>{t.analytics.payoutsDeleteExpenseTotal}</span>
                <span className="font-mono font-semibold text-pnlNegative">-{formatMoney(total)}</span>
              </li>
            )}
          </ul>

          <p className="text-sm text-muted-foreground">{t.analytics.payoutsDeleteExpenseKeepsAccount}</p>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={deleting}>
              {t.analytics.payoutsDeleteExpenseCancel}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" aria-hidden />
              {deleting ? "..." : t.analytics.payoutsDeleteExpenseConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
